// Risk Level Normalization Utility
// 5-level system matching Dreaded Event gravity:
// minor (Mineure), moderate (Modérée), major (Majeure), critical (Critique), catastrophic (Catastrophique)

export const RISK_LEVELS = {
  MINOR: "minor",
  MODERATE: "moderate",
  MAJOR: "major",
  CRITICAL: "critical",
  CATASTROPHIC: "catastrophic",
};

const _debugSeen = new Set();

export const normalizeRiskLevel = (level) => {
  if (level === null || level === undefined || level === "") {
    return null;
  }

  // Normalize Unicode to handle different é encodings (U+00E9 vs U+0065+U+0301)
  const str = String(level).toLowerCase().trim().normalize("NFC");

  let result = null;

  // Numeric scale 1-5
  if (str === "5") result = RISK_LEVELS.CATASTROPHIC;
  else if (str === "4") result = RISK_LEVELS.CRITICAL;
  else if (str === "3") result = RISK_LEVELS.MAJOR;
  else if (str === "2") result = RISK_LEVELS.MODERATE;
  else if (str === "1") result = RISK_LEVELS.MINOR;
  // French labels - Operational Scenario format (DB enum values)
  else if (
    str === "très élevé" ||
    str === "très élevée" ||
    str === "très eleve"
  )
    result = RISK_LEVELS.CATASTROPHIC;
  else if (
    str === "élevé" ||
    str === "élevée" ||
    str === "eleve" ||
    str === "elevee"
  )
    result = RISK_LEVELS.CRITICAL;
  else if (str === "moyen" || str === "moyenne") result = RISK_LEVELS.MAJOR;
  else if (str === "faible") result = RISK_LEVELS.MODERATE;
  else if (str === "très faible") result = RISK_LEVELS.MINOR;
  // French labels - Dreaded Event gravity format
  else if (str === "catastrophique") result = RISK_LEVELS.CATASTROPHIC;
  else if (str === "critique") result = RISK_LEVELS.CRITICAL;
  else if (str === "majeure" || str === "majeur") result = RISK_LEVELS.MAJOR;
  else if (str === "modérée" || str === "modéré" || str === "moderee")
    result = RISK_LEVELS.MODERATE;
  else if (str === "mineure" || str === "mineur") result = RISK_LEVELS.MINOR;
  // English labels (5-level)
  else if (str === "catastrophic") result = RISK_LEVELS.CATASTROPHIC;
  else if (str === "critical") result = RISK_LEVELS.CRITICAL;
  else if (str === "major") result = RISK_LEVELS.MAJOR;
  else if (str === "moderate") result = RISK_LEVELS.MODERATE;
  else if (str === "minor") result = RISK_LEVELS.MINOR;
  // Legacy 4-level English labels (backward compatibility)
  else if (
    str === "high" ||
    str === "veryhigh" ||
    str === "very_high" ||
    str === "very high"
  )
    result = RISK_LEVELS.CRITICAL;
  else if (str === "medium") result = RISK_LEVELS.MAJOR;
  else if (str === "low") result = RISK_LEVELS.MODERATE;
  else if (
    str === "verylow" ||
    str === "very_low" ||
    str === "very low" ||
    str === "negligible"
  )
    result = RISK_LEVELS.MINOR;
  // Safe partial patterns - only for variations not covered by exact matches
  else if (str.includes("catastroph") || str.includes("très élev"))
    result = RISK_LEVELS.CATASTROPHIC;
  else if (str.includes("criti")) result = RISK_LEVELS.CRITICAL;
  else if (str.includes("majeur") || str.includes("maje"))
    result = RISK_LEVELS.MAJOR;
  else if (str.includes("élev") || str.includes("elev"))
    result = RISK_LEVELS.CRITICAL;
  else if (
    str.includes("moyen") ||
    str.includes("modér") ||
    str.includes("moder")
  )
    result = RISK_LEVELS.MAJOR;
  else if (str.includes("faible") || str.includes("bas"))
    result = RISK_LEVELS.MODERATE;
  else if (
    str.includes("mineur") ||
    str.includes("négligeable") ||
    str.includes("negligeable")
  )
    result = RISK_LEVELS.MINOR;

  // Log unique input→output pairs
  const key = `${JSON.stringify(level)}→${result}`;
  if (!_debugSeen.has(key)) {
    _debugSeen.add(key);
    console.log(
      "[riskLevelNormalizer]",
      JSON.stringify(level),
      "(normalized:",
      JSON.stringify(str) + ")",
      "→",
      result,
    );
  }

  if (result === null) {
    console.warn(
      "[riskLevelNormalizer] UNMATCHED input:",
      JSON.stringify(level),
      "| normalizedName:",
      JSON.stringify(str),
    );
  }

  return result;
};

export const normalizeScenario = (scenario) => {
  if (!scenario) return scenario;

  return {
    ...scenario,
    severity: normalizeRiskLevel(scenario.severity),
    likelihood: normalizeRiskLevel(scenario.likelihood),
  };
};

export const normalizeScenarios = (scenarios) => {
  return scenarios.map(normalizeScenario);
};

export const calculateDistributions = (scenarios) => {
  const normalized = normalizeScenarios(scenarios);

  const severityDistribution = {
    catastrophic: normalized.filter(
      (s) => s.severity === RISK_LEVELS.CATASTROPHIC,
    ).length,
    critical: normalized.filter((s) => s.severity === RISK_LEVELS.CRITICAL)
      .length,
    major: normalized.filter((s) => s.severity === RISK_LEVELS.MAJOR).length,
    moderate: normalized.filter((s) => s.severity === RISK_LEVELS.MODERATE)
      .length,
    minor: normalized.filter((s) => s.severity === RISK_LEVELS.MINOR).length,
  };

  const likelihoodDistribution = {
    catastrophic: normalized.filter(
      (s) => s.likelihood === RISK_LEVELS.CATASTROPHIC,
    ).length,
    critical: normalized.filter((s) => s.likelihood === RISK_LEVELS.CRITICAL)
      .length,
    major: normalized.filter((s) => s.likelihood === RISK_LEVELS.MAJOR).length,
    moderate: normalized.filter((s) => s.likelihood === RISK_LEVELS.MODERATE)
      .length,
    minor: normalized.filter((s) => s.likelihood === RISK_LEVELS.MINOR).length,
  };

  return { severityDistribution, likelihoodDistribution };
};

export const getSeverityLabelFr = (normalizedLevel) => {
  const labels = {
    [RISK_LEVELS.CATASTROPHIC]: "Catastrophique",
    [RISK_LEVELS.CRITICAL]: "Critique",
    [RISK_LEVELS.MAJOR]: "Majeure",
    [RISK_LEVELS.MODERATE]: "Modérée",
    [RISK_LEVELS.MINOR]: "Mineure",
  };
  return labels[normalizedLevel] || "N/A";
};

export const getLikelihoodLabelFr = (normalizedLevel) => {
  const labels = {
    [RISK_LEVELS.CATASTROPHIC]: "Quasi certain",
    [RISK_LEVELS.CRITICAL]: "Quasi certain",
    [RISK_LEVELS.MAJOR]: "Très vraisemblable",
    [RISK_LEVELS.MODERATE]: "Vraisemblable",
    [RISK_LEVELS.MINOR]: "Peu vraisemblable",
  };
  return labels[normalizedLevel] || "N/A";
};

export const getSeverityLabelEn = (normalizedLevel) => {
  const labels = {
    [RISK_LEVELS.CATASTROPHIC]: "Catastrophic",
    [RISK_LEVELS.CRITICAL]: "Critical",
    [RISK_LEVELS.MAJOR]: "Major",
    [RISK_LEVELS.MODERATE]: "Moderate",
    [RISK_LEVELS.MINOR]: "Minor",
  };
  return labels[normalizedLevel] || "N/A";
};

export const getLikelihoodLabelEn = (normalizedLevel) => {
  const labels = {
    [RISK_LEVELS.CATASTROPHIC]: "Almost certain",
    [RISK_LEVELS.CRITICAL]: "Almost certain",
    [RISK_LEVELS.MAJOR]: "Very likely",
    [RISK_LEVELS.MODERATE]: "Likely",
    [RISK_LEVELS.MINOR]: "Unlikely",
  };
  return labels[normalizedLevel] || "N/A";
};

export const getSeverityColor = (normalizedLevel) => {
  const colors = {
    [RISK_LEVELS.CATASTROPHIC]: [31, 41, 55],
    [RISK_LEVELS.CRITICAL]: [220, 38, 38],
    [RISK_LEVELS.MAJOR]: [234, 88, 12],
    [RISK_LEVELS.MODERATE]: [234, 179, 8],
    [RISK_LEVELS.MINOR]: [34, 197, 94],
  };
  return colors[normalizedLevel] || [100, 116, 139];
};

export const enrichScenariosWithDreadedEventSeverity = (
  scenarios,
  attackPaths,
  dreadedEvents,
) => {
  if (!scenarios || !Array.isArray(scenarios)) return scenarios;
  if (!attackPaths || !Array.isArray(attackPaths)) return scenarios;
  if (!dreadedEvents || !Array.isArray(dreadedEvents)) return scenarios;

  const eventMap = new Map();
  dreadedEvents.forEach((evt) => {
    if (evt && evt.id) {
      eventMap.set(String(evt.id), evt);
    }
  });

  return scenarios.map((scenario) => {
    // Match by full id first, then fall back to referenceCode (e.g. "CA01") or name
    const attackPath = attackPaths.find(
      (ap) => ap && (
        ap.id === scenario.attackPathId ||
        ap.referenceCode === scenario.attackPathId ||
        ap.referenceCode === scenario.attackPathReference ||
        ap.name === scenario.attackPathId
      ),
    );

    let severityFromDreadedEvent = null;

    if (attackPath) {
      // Try attack path's severity first (already propagated from dreaded event)
      if (attackPath.severity) {
        severityFromDreadedEvent = attackPath.severity;
      }
      // If attack path has dreadedEventId, look up the dreaded event directly
      else if (attackPath.dreadedEventId) {
        const dreadedEvent = eventMap.get(String(attackPath.dreadedEventId));
        if (dreadedEvent && dreadedEvent.severity) {
          severityFromDreadedEvent = dreadedEvent.severity;
        }
      }
    }

    // Build enriched context from live attack path data (overrides stale AI-generated text)
    let enrichedContext = scenario.context || {};
    let attackPathReference = scenario.attackPathReference;

    if (attackPath) {
      // Always use live attack path data for context fields - this is the source of truth
      enrichedContext = {
        ...enrichedContext,
        // Dreaded event name: prefer dreadedEventName from attack path
        dreadedEvent: attackPath.dreadedEventName || enrichedContext.dreadedEvent,
        // Source risk / threat source
        sourceRisk: attackPath.sourceRiskName || enrichedContext.sourceRisk,
        // Target objective
        targetObjective: attackPath.objectifVise || enrichedContext.targetObjective,
      };
      // Attack path reference code (e.g. "CA01")
      attackPathReference = attackPath.referenceCode || attackPath.name || attackPathReference;
    }

    // If we have a severity from the dreaded event, use it; otherwise keep scenario's own severity
    const enriched = {
      ...scenario,
      context: enrichedContext,
      attackPathReference,
      severitySource: severityFromDreadedEvent ? "dreadedEvent" : "scenario",
    };

    if (severityFromDreadedEvent) {
      enriched.severity = severityFromDreadedEvent;
    }

    return enriched;
  });
};
